import { type SVGProps, useMemo } from 'react'
import { useUIStore } from '@/stores/uiStore'

interface LogoProps extends SVGProps<SVGSVGElement> {
  /**
   * Override the primary color (default: brand color or white in dark mode)
   */
  primary?: string
  /**
   * Override the secondary/text color
   */
  secondary?: string
  /**
   * Width of the logo
   */
  width?: string | number
  /**
   * Force logo type (auto-detects from hostname by default)
   */
  type?: 'pollie' | 'bonnie'
  /**
   * Show only the icon version (no text)
   */
  iconOnly?: boolean
}

export function Logo({
  primary,
  secondary,
  width = '120',
  type,
  iconOnly = false,
  ...props
}: LogoProps) {
  const resolvedTheme = useUIStore((state) => state.resolvedTheme)
  const isDark = resolvedTheme === 'dark'

  // Default colors based on theme
  const primaryColor = primary || (isDark ? '#FFFFFF' : '#368f8b')
  const secondaryColor = secondary || (isDark ? '#FFFFFF' : '#172121')

  // Auto-detect logo type from hostname
  const logoType = useMemo(() => {
    if (type) return type
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname
      if (hostname.includes('pollie')) return 'pollie'
    }
    return 'bonnie'
  }, [type])

  // Icon-only version (for collapsed sidebar)
  if (iconOnly) {
    return (
      <svg
        width={width}
        viewBox="0 0 300 285"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
      >
        <mask
          id="mask0_icon"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width="285"
          height="285"
          style={{ maskType: 'alpha' }}
        >
          <circle cx="142.5" cy="142.5" r="142.5" fill="#D9D9D9" />
        </mask>
        <g mask="url(#mask0_icon)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M300 1C300 -1.76142 297.762 -4 295 -4H5.00012C2.2387 -4 0.00012207 -1.76142 0.00012207 1V95C0.00012207 97.7614 2.2387 100 5.00012 100H149.102C150.428 100 151.7 100.527 152.637 101.464L197.264 146.091C199.216 148.043 202.382 148.043 204.335 146.091L248.961 101.464C249.898 100.527 251.17 100 252.496 100H295C297.762 100 300 97.7614 300 95V1Z"
            fill={primaryColor}
          />
          <path
            opacity=".22"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M-5.84051 284.088C-5.84051 286.85 -3.60193 289.088 -0.840509 289.088L282.92 289.088C285.681 289.088 287.92 286.85 287.92 284.088L287.92 189.549C287.92 186.788 285.681 184.549 282.92 184.549L144.921 184.549C143.594 184.549 142.323 184.022 141.385 183.085L97.0806 138.78C95.128 136.828 91.9621 136.828 90.0095 138.78L45.7051 183.085C44.7674 184.022 43.4956 184.549 42.1695 184.549L-0.840506 184.549C-3.60193 184.549 -5.84051 186.788 -5.84051 189.549L-5.84051 284.088Z"
            fill={primaryColor}
          />
        </g>
      </svg>
    )
  }

  // Full logo with text
  if (logoType === 'pollie') {
    return (
      <svg
        data-name="Layer 2"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 473.52 128.7"
        width={width}
        {...props}
      >
        <g data-name="Logo">
          <path
            fill={secondaryColor}
            d="M153.9 128.24v-98h10.5l.7 12.88v85.12h-11.2Zm34.86-26.32c-5.88 0-11.04-1.54-15.47-4.62-4.43-3.08-7.86-7.4-10.29-12.95-2.43-5.55-3.64-11.92-3.64-19.11s1.21-13.67 3.64-19.18 5.86-9.8 10.29-12.88c4.43-3.08 9.59-4.62 15.47-4.62 6.25 0 11.76 1.54 16.52 4.62s8.47 7.37 11.13 12.88c2.66 5.51 3.99 11.9 3.99 19.18s-1.33 13.56-3.99 19.11c-2.66 5.55-6.37 9.87-11.13 12.95-4.76 3.08-10.27 4.62-16.52 4.62Zm-2.52-10.22c4.39 0 8.28-1.12 11.69-3.36 3.41-2.24 6.11-5.34 8.12-9.31 2.01-3.97 3.01-8.56 3.01-13.79s-.96-9.71-2.87-13.72c-1.91-4.01-4.57-7.14-7.98-9.38-3.41-2.24-7.35-3.36-11.83-3.36-4.11 0-7.77 1.12-10.99 3.36-3.22 2.24-5.74 5.34-7.56 9.31-1.82 3.97-2.73 8.56-2.73 13.79s.91 9.71 2.73 13.72c1.82 4.01 4.32 7.14 7.49 9.38 3.17 2.24 6.81 3.36 10.92 3.36Zm80.78 10.22c-6.91 0-13-1.56-18.27-4.69-5.27-3.13-9.4-7.44-12.39-12.95-2.99-5.51-4.48-11.85-4.48-19.04s1.47-13.53 4.41-19.04c2.94-5.51 7.05-9.82 12.32-12.95 5.27-3.13 11.32-4.69 18.13-4.69s12.86 1.56 18.13 4.69c5.27 3.13 9.38 7.44 12.32 12.95 2.94 5.51 4.41 11.85 4.41 19.04s-1.47 13.53-4.41 19.04c-2.94 5.51-7.02 9.82-12.25 12.95-5.23 3.12-11.2 4.69-17.92 4.69Zm-.14-10.36c4.57 0 8.61-1.12 12.11-3.36s6.25-5.34 8.26-9.31c2.01-3.97 3.01-8.52 3.01-13.65s-1-9.68-3.01-13.65c-2.01-3.97-4.78-7.07-8.33-9.31s-7.65-3.36-12.32-3.36-8.61 1.12-12.11 3.36c-3.5 2.24-6.25 5.32-8.26 9.24s-3.01 8.49-3.01 13.72 1 9.68 3.01 13.65c2.01 3.97 4.81 7.07 8.4 9.31 3.59 2.24 7.68 3.36 12.25 3.36Zm50.26 8.68v-98h11.2v98h-11.2Zm30.8 0v-98h11.2v98h-11.2ZM385.18 14c-1.87 0-3.5-.7-4.9-2.1-1.4-1.4-2.1-3.03-2.1-4.9 0-1.96.7-3.62 2.1-4.97 1.4-1.35 3.03-2.03 4.9-2.03 1.96 0 3.62.68 4.97 2.03 1.35 1.35 2.03 3.01 2.03 4.97s-.68 3.5-2.03 4.9c-1.35 1.4-3.01 2.1-4.97 2.1Zm-5.74 86.24v-70h11.2v70h-11.2Zm62.44 1.68c-6.81 0-12.83-1.56-18.06-4.69-5.23-3.13-9.31-7.44-12.25-12.95-2.94-5.51-4.41-11.85-4.41-19.04s1.47-13.53 4.41-19.04c2.94-5.51 7-9.82 12.18-12.95 5.18-3.13 11.13-4.69 17.85-4.69s12.18 1.61 16.94 4.83c4.76 3.22 8.45 7.72 11.06 13.51 2.61 5.79 3.92 12.6 3.92 20.44H415.7l2.8-2.38c0 5.6 1 10.41 3.01 14.42s4.83 7.07 8.47 9.17c3.64 2.1 7.79 3.15 12.46 3.15 4.95 0 9.12-1.17 12.53-3.5s6.04-5.41 7.91-9.24l9.66 4.9a31.658 31.658 0 0 1-7.07 9.52c-2.94 2.71-6.39 4.81-10.36 6.3-3.97 1.49-8.38 2.24-13.23 2.24ZM419.2 60.2l-2.94-2.24h48.3l-2.94 2.38c0-4.48-.89-8.35-2.66-11.62-1.77-3.27-4.15-5.79-7.14-7.56-2.99-1.77-6.44-2.66-10.36-2.66s-7.44.89-10.85 2.66c-3.41 1.77-6.16 4.27-8.26 7.49-2.1 3.22-3.15 7.07-3.15 11.55Z"
          />
          <path
            stroke={primaryColor}
            strokeMiterlimit="10"
            fill={primaryColor}
            d="M112.46 47.8c2.26-2.26 11.29-1.13 13.55-2.26-.13-12.44.28-26.73.41-39.17.03-2.86-.93-3.68-3.73-3.68-39.48.07-78.95.07-118.43 0-2.78 0-3.77.79-3.74 3.66.13 12.2.17 24.41-.02 36.6-.06 3.47.99 4.46 4.45 4.44 21.17-.15 42.35 0 63.52-.16 3.43-.03 5.84.99 8.12 3.45 4.45 4.79 9.15 9.35 14.51 14.78l21.36-17.68Z"
          />
          <path
            fill={primaryColor}
            opacity=".22"
            d="M14.46 83.59c-1.98.58-4.25 1.14-6.77 1.55-2.47.41-4.74.62-6.77.71.13 12.44-.28 26.73-.41 39.17-.03 2.86.93 3.68 3.73 3.68 39.48-.07 78.95-.07 118.43 0 2.78 0 3.77-.79 3.74-3.66-.13-12.2-.17-24.41.02-36.6.06-3.47-.99-4.46-4.45-4.44-21.17.15-42.35 0-63.52.16-1.78.01-3.29-.25-4.64-.86-1.89-.84-3.03-2.1-3.48-2.59-2.34-2.55-7.53-7.77-14.51-14.78-7.12 5.89-14.24 11.79-21.36 17.68Z"
          />
        </g>
      </svg>
    )
  }

  // Bonnie logo (default)
  return (
    <svg
      width={width}
      viewBox="0 0 1153 285"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1093.22 235.072C1080.76 235.072 1069.76 232.256 1060.2 226.624C1050.64 220.821 1043.13 212.885 1037.67 202.816C1032.21 192.747 1029.48 181.141 1029.48 168C1029.48 154.859 1032.21 143.253 1037.67 133.184C1043.13 123.115 1050.64 115.264 1060.2 109.632C1069.93 103.829 1081.02 100.928 1093.48 100.928C1105.25 100.928 1115.58 103.915 1124.45 109.888C1133.33 115.691 1140.24 124.053 1145.19 134.976C1150.31 145.728 1152.87 158.443 1152.87 173.12H1049.45L1054.05 169.28C1054.05 178.325 1055.85 186.176 1059.43 192.832C1063.01 199.488 1067.88 204.608 1074.02 208.192C1080.17 211.605 1087.16 213.312 1095.01 213.312C1103.55 213.312 1110.63 211.349 1116.26 207.424C1122.06 203.328 1126.59 198.208 1129.83 192.064L1150.82 201.792C1147.41 208.448 1142.97 214.336 1137.51 219.456C1132.05 224.405 1125.56 228.245 1118.05 230.976C1110.72 233.707 1102.44 235.072 1093.22 235.072ZM1055.59 157.76L1050.73 153.92H1131.62L1127.01 157.76C1127.01 150.251 1125.48 143.851 1122.41 138.56C1119.33 133.099 1115.24 129.003 1110.12 126.272C1105 123.371 1099.28 121.92 1092.97 121.92C1086.82 121.92 1080.85 123.371 1075.05 126.272C1069.41 129.003 1064.72 133.013 1060.97 138.304C1057.38 143.595 1055.59 150.08 1055.59 157.76Z"
        fill={secondaryColor}
      />
      <path
        d="M976.682 232V104H1001V232H976.682ZM988.97 76.864C985.045 76.864 981.631 75.4133 978.73 72.5119C975.829 69.4399 974.378 65.9413 974.378 62.0159C974.378 57.9199 975.829 54.5066 978.73 51.7759C981.631 48.8746 985.045 47.424 988.97 47.424C993.066 47.424 996.479 48.8746 999.21 51.7759C1002.11 54.5066 1003.56 57.9199 1003.56 62.0159C1003.56 65.9413 1002.11 69.4399 999.21 72.5119C996.479 75.4133 993.066 76.864 988.97 76.864Z"
        fill={secondaryColor}
      />
      <path
        d="M831.646 232V104H854.43L855.966 127.552V232H831.646ZM920.222 232V166.464H944.542V232H920.222ZM920.222 166.464C920.222 154.859 918.857 146.069 916.126 140.096C913.566 133.952 909.897 129.685 905.118 127.296C900.51 124.907 895.134 123.627 888.99 123.456C878.579 123.456 870.473 127.125 864.67 134.464C858.867 141.803 855.966 152.128 855.966 165.44H845.47C845.47 151.957 847.433 140.437 851.358 130.88C855.454 121.152 861.171 113.728 868.51 108.608C876.019 103.488 884.894 100.928 895.134 100.928C905.203 100.928 913.907 102.976 921.246 107.072C928.755 111.168 934.473 117.568 938.398 126.272C942.494 134.805 944.542 146.069 944.542 160.064V166.464H920.222Z"
        fill={secondaryColor}
      />
      <path
        d="M688.146 232V104H710.93L712.466 127.552V232H688.146ZM776.722 232V166.464H801.042V232H776.722ZM776.722 166.464C776.722 154.859 775.357 146.069 772.626 140.096C770.066 133.952 766.397 129.685 761.618 127.296C757.01 124.907 751.634 123.627 745.49 123.456C735.079 123.456 726.973 127.125 721.17 134.464C715.367 141.803 712.466 152.128 712.466 165.44H701.97C701.97 151.957 703.933 140.437 707.858 130.88C711.954 121.152 717.671 113.728 725.01 108.608C732.519 103.488 741.394 100.928 751.634 100.928C761.703 100.928 770.407 102.976 777.746 107.072C785.255 111.168 790.973 117.568 794.898 126.272C798.994 134.805 801.042 146.069 801.042 160.064V166.464H776.722Z"
        fill={secondaryColor}
      />
      <path
        d="M597.502 235.072C584.702 235.072 573.438 232.256 563.71 226.624C553.982 220.821 546.302 212.885 540.67 202.816C535.209 192.747 532.478 181.141 532.478 168C532.478 154.859 535.209 143.253 540.67 133.184C546.131 123.115 553.726 115.264 563.454 109.632C573.182 103.829 584.361 100.928 596.99 100.928C609.619 100.928 620.798 103.829 630.526 109.632C640.254 115.264 647.849 123.115 653.31 133.184C658.771 143.253 661.502 154.859 661.502 168C661.502 181.141 658.771 192.747 653.31 202.816C647.849 212.885 640.254 220.821 630.526 226.624C620.969 232.256 609.961 235.072 597.502 235.072ZM597.502 212.8C605.182 212.8 612.009 210.923 617.982 207.168C623.955 203.243 628.563 197.952 631.806 191.296C635.219 184.64 636.926 176.875 636.926 168C636.926 159.125 635.219 151.36 631.806 144.704C628.563 138.048 623.87 132.843 617.726 129.088C611.582 125.163 604.67 123.2 596.99 123.2C589.139 123.2 582.227 125.163 576.254 129.088C570.281 132.843 565.587 138.048 562.174 144.704C558.761 151.36 557.054 159.125 557.054 168C557.054 176.875 558.761 184.64 562.174 191.296C565.587 197.952 570.366 203.243 576.51 207.168C582.654 210.923 589.651 212.8 597.502 212.8Z"
        fill={secondaryColor}
      />
      <path
        d="M388.896 232V52.7999H413.216V208.192L411.936 232H388.896ZM455.2 235.072C444.448 235.072 435.061 232.256 427.04 226.624C419.189 220.992 413.131 213.141 408.864 203.072C404.597 193.003 402.464 181.312 402.464 168C402.464 154.517 404.597 142.827 408.864 132.928C413.131 122.859 419.189 115.008 427.04 109.376C435.061 103.744 444.448 100.928 455.2 100.928C466.635 100.928 476.704 103.744 485.408 109.376C494.112 115.008 500.853 122.859 505.632 132.928C510.581 142.827 513.056 154.517 513.056 168C513.056 181.312 510.581 193.003 505.632 203.072C500.853 213.141 494.112 220.992 485.408 226.624C476.704 232.256 466.635 235.072 455.2 235.072ZM449.312 213.056C456.992 213.056 463.733 211.179 469.536 207.424C475.339 203.499 479.861 198.208 483.104 191.552C486.517 184.725 488.224 176.875 488.224 168C488.224 159.125 486.603 151.36 483.36 144.704C480.117 137.877 475.595 132.587 469.792 128.832C463.989 124.907 457.248 122.944 449.568 122.944C442.571 122.944 436.256 124.907 430.624 128.832C425.163 132.587 420.896 137.877 417.824 144.704C414.752 151.531 413.216 159.296 413.216 168C413.216 176.875 414.752 184.725 417.824 191.552C420.896 198.208 425.163 203.499 430.624 207.424C436.085 211.179 442.315 213.056 449.312 213.056Z"
        fill={secondaryColor}
      />
      <mask
        id="mask0_546_244"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="285"
        height="285"
        style={{ maskType: 'alpha' }}
      >
        <circle cx="142.5" cy="142.5" r="142.5" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_546_244)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M300 1C300 -1.76142 297.762 -4 295 -4H5.00012C2.2387 -4 0.00012207 -1.76142 0.00012207 1V95C0.00012207 97.7614 2.2387 100 5.00012 100H149.102C150.428 100 151.7 100.527 152.637 101.464L197.264 146.091C199.216 148.043 202.382 148.043 204.335 146.091L248.961 101.464C249.898 100.527 251.17 100 252.496 100H295C297.762 100 300 97.7614 300 95V1Z"
          fill={primaryColor}
        />
        <path
          opacity=".22"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M-5.84051 284.088C-5.84051 286.85 -3.60193 289.088 -0.840509 289.088L282.92 289.088C285.681 289.088 287.92 286.85 287.92 284.088L287.92 189.549C287.92 186.788 285.681 184.549 282.92 184.549L144.921 184.549C143.594 184.549 142.323 184.022 141.385 183.085L97.0806 138.78C95.128 136.828 91.9621 136.828 90.0095 138.78L45.7051 183.085C44.7674 184.022 43.4956 184.549 42.1695 184.549L-0.840506 184.549C-3.60193 184.549 -5.84051 186.788 -5.84051 189.549L-5.84051 284.088Z"
          fill={primaryColor}
        />
      </g>
    </svg>
  )
}

export default Logo
